/**
 * Layout of the Form Builder's "Add element" menu, declared as groups.
 *
 * The menu used to be assembled by splicing helpers and dividers into the
 * element list at hard-coded indices, and the video lock looked for the video
 * element at a fixed index — so adding one entry silently shifted every
 * divider and dropped the lock. Here each group lists element keys (from
 * `config/available-elements.js`) or helper ids; a divider separates groups.
 */

/** Helper entries: menu items that are not a single element template. */
export const MENU_HELPER = Object.freeze({
    CONTACT_INFO: 'bulk-contact-info',
    CONSENT: 'consent-helper',
    IMPORT_QUESTIONS: 'import-questions-helper'
});

export const VIDEO_ELEMENT_KEY = 'AddPipeInput';

export const ADD_ELEMENT_MENU_GROUPS = Object.freeze([
    ['Header', 'Paragraph'],
    [MENU_HELPER.CONTACT_INFO, MENU_HELPER.CONSENT],
    ['Checkboxes', 'RadioButtons', 'Dropdown', 'Matrix'],
    ['TextInput', 'TextArea', 'NumberInput', 'HiddenValue'],
    ['Rating', 'RangeElement'],
    ['DatePicker'],
    [VIDEO_ELEMENT_KEY, 'Signature', 'FileUpload', 'Captcha'],
    [MENU_HELPER.IMPORT_QUESTIONS]
]);

/**
 * Build the NiceDropdown items for the "Add element" menu.
 *
 * @param {Object} options
 * @param {Array<{key: string, name: string, icon: *}>} options.elements  element templates
 * @param {Object<string, Object>} options.helpers  NiceDropdown items keyed by helper id
 *   (`MENU_HELPER`); a helper that is not supplied is left out
 * @param {Object|null} [options.videoLock]  when set, the video element is disabled and
 *   gets these extra item props (e.g. `{ suffix }`)
 * @param {Array<Array<string>>} [options.groups]  menu layout
 * @returns {Array<Object>} NiceDropdown items, dividers included
 */
export function buildAddElementMenu({ elements, helpers, videoLock = null, groups = ADD_ELEMENT_MENU_GROUPS }) {
    const elementItems = {};
    elements.forEach(item => {
        elementItems[item.key] = { id: item.key, title: item.name, icon: item.icon };
    });

    const menuGroups = groups
        .map(group =>
            group
                .map(id => {
                    if (helpers[id]) return helpers[id];
                    const item = elementItems[id];
                    if (!item) return null;
                    if (id === VIDEO_ELEMENT_KEY && videoLock) {
                        return { ...item, ...videoLock, disabled: true };
                    }
                    return item;
                })
                .filter(Boolean)
        )
        .filter(group => group.length > 0);

    const items = [];
    menuGroups.forEach((group, index) => {
        if (index > 0) items.push({ id: `divider-${index - 1}`, divider: true });
        items.push(...group);
    });
    return items;
}
